import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Chip, Alert } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { formatDate, formatTime } from "utils/formatters";
import { groupContractHistoryByDate } from "../../utils/groupContractHistoryByDate";
import {
  getContractItemHistory,
  getChangeTypeDisplayName,
  getChangeTypeIcon,
  getChangeTypeColor,
} from "../../services/contractItemHistory.service";
import DataTable from "components/common/tables/DataTable";

const ContractItemHistory = ({ contractId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define columns for Material React Table
  const columns = useMemo(
    () => [
      {
        accessorKey: "changedAt",
        header: "Thời gian",
        size: 100,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return (
              <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>
                {formatDate(cell.getValue())}
              </Typography>
            );
          }
          // return formatTimeOnly(cell.getValue());
          return formatTime(cell.getValue());
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "changeType",
        header: "Loại thay đổi",
        size: 130,
        enableSorting: false,
        enableGlobalFilter: true,
        accessorFn: (row) => {
          if (row.isGroup) {
            return `${row.changeCount} thay đổi`;
          }
          // Return both original value and display name for search
          const displayName = getChangeTypeDisplayName(row.changeType);
          return `${row.changeType} ${displayName}`;
        },
        Cell: ({ row }) => {
          if (row.original.isGroup) {
            return (
              <Typography sx={{ fontWeight: "bold", fontSize: "12px" }}>
                {row.original.changeCount} thay đổi
              </Typography>
            );
          }

          const changeType = row.original.changeType;
          return (
            <Chip
              icon={
                <span style={{ fontSize: "12px" }}>
                  {getChangeTypeIcon(changeType)}
                </span>
              }
              label={getChangeTypeDisplayName(changeType)}
              size="small"
              sx={{
                bgcolor: getChangeTypeColor(changeType) + "20",
                color: getChangeTypeColor(changeType),
                border: `1px solid ${getChangeTypeColor(changeType)}40`,
              }}
            />
          );
        },
      },
      {
        accessorKey: "itemCode",
        header: "Mã hạng mục",
        size: 100,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue();
        },
      },
      {
        accessorKey: "itemName",
        header: "Tên hạng mục",
        size: 200,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return (
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "200px",
                fontSize: "12px",
              }}
            >
              {cell.getValue()}
            </Typography>
          );
        },
      },
      {
        accessorKey: "fieldDisplayName",
        header: "Trường thay đổi",
        size: 120,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
      },
      {
        accessorKey: "oldValue",
        header: "Giá trị cũ",
        size: 180,
        enableSorting: false,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "newValue",
        header: "Giá trị mới",
        size: 180,
        enableSorting: false,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "changedBy",
        header: "Người thay đổi",
        size: 90,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue();
        },
      },
    ],
    []
  );

  // Fetch data
  const fetchHistory = useCallback(async () => {
    if (!contractId) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch all data at once for client-side filtering/sorting
      const response = await getContractItemHistory(contractId, 1, 1000);
      const groupedData = groupContractHistoryByDate(response.items || []);
      setData(groupedData);
    } catch (err) {
      setError("Không thể tải lịch sử hạng mục: " + err.message);
      console.error("Error fetching contract item history:", err);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (!contractId) {
    return (
      <Alert severity="warning">
        Vui lòng chọn một hợp đồng để xem lịch sử hạng mục.
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={data}
        enableColumnActions={false}
        enableColumnFilterModes={false}
        enableColumnOrdering={false}
        enableGrouping={false}
        enablePinning={true}
        enableRowSelection={false}
        enableColumnResizing={true}
        enableExpanding={true}
        enableExpandAll={false}
        columnFilterDisplayMode="popover"
        paginationDisplayMode="pages"
        positionToolbarAlertBanner="bottom"
        enableGlobalFilter={true}
        globalFilterFn="includesString"
        enableSorting={false}
        enablePagination={true}
        getSubRows={(row) => row.subRows || []}
        filterFromLeafRows={true}
        state={{
          isLoading: loading
        }}
        initialState={{
          showColumnFilters: false,
          showGlobalFilter: true,
          pagination: {
            pageIndex: 0,
            pageSize: 25,
          },
          expanded: false,
        }}
      />
    </Box>
  );
};

export default ContractItemHistory;
